import { ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import { getFinancePayment } from '../../api/finance';
import { approvePayment, rejectPayment, reversePayment } from '../../api/payments';
import { PaymentEvidenceList } from '../../components/payments/PaymentEvidenceList';
import { RequestReceiptDialog } from '../../components/payments/RequestReceiptDialog';
import { DeletePaymentDialog } from '../../components/payments/DeletePaymentDialog';
import { useAuth } from '../../context/authContext';
import {
  DocumentPreviewDialog,
  DocumentPreviewTarget,
} from '../../components/DocumentPreviewDialog';
import { Payment } from '../../types/payment';
import { ApprovalStatusChip } from './ApprovalStatusChip';
import { formatDate, formatMoney } from './financeFormat';
import { describeNumber } from '../../components/temporaryNumber';

const errorMessage = (err: unknown, fallback: string) =>
  isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

const formatDateTime = (value: string | null) =>
  value
    ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

/** Review one payment (Payment Confirmations, Payment Ledger): every
 * detail the reviewer needs, the slip, the review history, and the actions
 * its status allows — Approve / Reject (reason required) while pending or
 * after a reversal, Reverse approval (reason required, payments:reverse)
 * once approved. The server is the authority — it refuses a payment
 * already decided (409) and a non-finance reviewer's own submission (403);
 * the buttons only mirror that. */

/** Roles allowed to review a payment they recorded (2026-11-02) — mirrors the API. */
const SELF_REVIEW_ROLES = ['Super Admin', 'Finance Officer', 'Finance Admin'];
const HISTORY_LABEL = { APPROVED: 'Approved', REJECTED: 'Rejected', REVERSED: 'Approval reversed' } as const;
export const PaymentReviewDialog = ({
  paymentId,
  onClose,
  onDecided,
}: {
  paymentId: number | null;
  onClose: () => void;
  onDecided: (payment: Payment, decision: 'approved' | 'rejected' | 'reversed' | 'deleted') => void;
}) => {
  const { user, hasPermission } = useAuth();
  const [rejecting, setRejecting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<DocumentPreviewTarget | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const query = useQuery({
    queryKey: ['finance-payment', paymentId],
    queryFn: () => getFinancePayment(paymentId!),
    enabled: paymentId != null,
  });
  const p = query.data;

  const close = () => {
    setRejecting(false);
    setReversing(false);
    setReason('');
    approveMutation.reset();
    rejectMutation.reset();
    reverseMutation.reset();
    onClose();
  };

  const approveMutation = useMutation({
    mutationFn: (id: number) => approvePayment(id),
    onSuccess: (payment) => {
      onDecided(payment, 'approved');
      close();
    },
  });
  const rejectMutation = useMutation({
    mutationFn: (id: number) => rejectPayment(id, reason.trim()),
    onSuccess: (payment) => {
      onDecided(payment, 'rejected');
      close();
    },
  });

  const reverseMutation = useMutation({
    mutationFn: (id: number) => reversePayment(id, reason.trim()),
    onSuccess: (payment) => {
      onDecided(payment, 'reversed');
      close();
    },
  });

  const isOwnSubmission = !!p && !!user && p.recordedById === user.id && !SELF_REVIEW_ROLES.includes(user.role.name);
  const reviewable = !!p && (p.status === 'PENDING' || p.status === 'REVERSED');
  const canReview = reviewable && hasPermission('payments', 'approve') && !isOwnSubmission;
  const canReverse = !!p && p.status === 'APPROVED' && hasPermission('payments', 'reverse');
  // 2026-11-04: pending or rejected payments only; approved ones are reversed.
  const canDelete = !!p && (p.status === 'PENDING' || p.status === 'REJECTED') && hasPermission('payments', 'delete');
  const busy = approveMutation.isPending || rejectMutation.isPending || reverseMutation.isPending;
  const mutationError = approveMutation.error ?? rejectMutation.error ?? reverseMutation.error;

  const rows: Array<[string, ReactNode]> = p
    ? [
        [
          'Student',
          <Link
            key="student"
            component={RouterLink}
            to={`/finance/students/${p.student.id}?paymentId=${p.id}`}
          >
            {p.student.name} ({describeNumber(p.student.studentNumber, 'student')})
          </Link>,
        ],
        ...(p.temporaryPaymentNumber && p.temporaryPaymentNumber !== p.paymentNumber
          ? ([['Temporary reference', p.temporaryPaymentNumber]] as Array<[string, string]>)
          : []),
        ['Branch', p.branch ?? '—'],
        [
          'Payment item',
          `${p.category.name}${p.type !== 'PAYMENT' ? ` (${p.type.toLowerCase()})` : ''}`,
        ],
        // 2026-11-04: an LKR payment entered SSCL-inclusive leads with the total entered.
        ...(p.totalInclSscl != null
          ? ([
              ['Total amount paid (incl. SSCL)', <strong key="total">{formatMoney(p.totalWithTax, p.currency)}</strong>],
              ['Amount excluding SSCL', formatMoney(p.amount, p.currency)],
              ['SSCL', formatMoney(p.taxAmount ?? 0, p.currency)],
            ] as Array<[string, ReactNode]>)
          : ([
              ['Amount', formatMoney(p.amount, p.currency)],
              ['SSCL', p.taxAmount == null ? '—' : formatMoney(p.taxAmount, p.currency)],
              ['Total incl. SSCL', <strong key="total">{formatMoney(p.totalWithTax, p.currency)}</strong>],
            ] as Array<[string, ReactNode]>)),
        [
          'Exchange rate / LKR',
          p.exchangeRate == null
            ? '—'
            : `${p.exchangeRate.toFixed(2)} · LKR ${(p.lkrAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        ],
        ...(p.exchangeRateEffectiveDate
          ? ([['Rate effective date', `${formatDate(p.exchangeRateEffectiveDate)} (receipt date)`]] as Array<[string, string]>)
          : []),
        ['Method', p.paymentMethod?.replace(/_/g, ' ').toLowerCase() ?? '—'],
        ['Reference', p.referenceNumber ?? '—'],
        ['Payment date', formatDate(p.paymentDate)],
        ['Submitted', `${formatDateTime(p.createdAt)} by ${p.recordedBy ?? '—'}`],
        ...(p.resubmittedFrom
          ? ([['Resubmission of', p.resubmittedFrom.paymentNumber]] as Array<[string, string]>)
          : []),
        ...(p.notes ? ([['Notes', p.notes]] as Array<[string, string]>) : []),
        ...(p.status === 'APPROVED' || p.status === 'REJECTED'
          ? ([
              [
                p.status === 'APPROVED' ? 'Approved' : 'Rejected',
                `${formatDateTime(p.approvedAt)} by ${p.approvedBy ?? '—'}`,
              ],
            ] as Array<[string, string]>)
          : []),
        ...(p.status === 'REVERSED'
          ? ([
              ['Reversed', `${formatDateTime(p.reversedAt)} by ${p.reversedBy ?? '—'}`],
              ['Reversal reason', p.reversalReason ?? '—'],
            ] as Array<[string, string]>)
          : []),
        ...(p.rejectionReason
          ? ([['Rejection reason', p.rejectionReason]] as Array<[string, string]>)
          : []),
        ...(p.status === 'DELETED'
          ? ([
              ['Deleted', `${formatDateTime(p.deletedAt ?? null)} by ${p.deletedBy ?? '—'}`],
              ['Deletion reason', p.deletionReason ?? '—'],
            ] as Array<[string, string]>)
          : []),
        ...(p.receipt ? ([['Receipt', p.receipt.receiptNumber]] as Array<[string, string]>) : []),
        ...(p.voidedReceipts.length
          ? ([['Voided receipts', p.voidedReceipts.map((r) => r.receiptNumber).join(', ')]] as Array<[string, string]>)
          : []),
      ]
    : [];

  return (
    <>
      <Dialog open={paymentId != null} onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <span>Review payment{p ? ` ${describeNumber(p.paymentNumber, 'payment')}` : ''}</span>
            {p && <ApprovalStatusChip status={p.status} />}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {query.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {query.isError && (
            <Alert severity="error">
              {errorMessage(query.error, "Couldn't load this payment.")}
            </Alert>
          )}
          {p && (
            <Stack spacing={2}>
              {(p.status === 'APPROVED' || p.status === 'REJECTED') && (
                <Alert severity={p.status === 'APPROVED' ? 'success' : 'warning'}>
                  This payment has already been {p.status === 'APPROVED' ? 'approved' : 'rejected'}.
                </Alert>
              )}
              {p.status === 'REVERSED' && (
                <Alert severity="warning">
                  This payment's approval was reversed — it doesn't count as paid
                  {p.voidedReceipts.length ? ` and its receipt ${p.voidedReceipts[p.voidedReceipts.length - 1].receiptNumber} is void` : ''}. It can be
                  approved again (a new receipt is issued) or rejected.
                </Alert>
              )}
              {reviewable && isOwnSubmission && (
                <Alert severity="info">
                  You recorded this payment, so another finance reviewer must approve or reject it.
                </Alert>
              )}
              <Table size="small">
                <TableBody>
                  {rows.map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell sx={{ width: 170, color: 'text.secondary', border: 0, py: 0.5 }}>
                        {label}
                      </TableCell>
                      <TableCell sx={{ border: 0, py: 0.5 }}>{value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Phase 107 — every receipt uploaded, oldest first; earlier
                  ones stay after a replacement. */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    Payment receipts
                  </Typography>
                  {p.status === 'PENDING' && hasPermission('payments', 'approve') && (
                    <Button size="small" startIcon={<AttachFileIcon />} onClick={() => setRequestOpen(true)}>
                      Request receipt
                    </Button>
                  )}
                </Stack>
                <PaymentEvidenceList
                  paymentId={p.id}
                  evidence={p.evidence ?? []}
                  onPreview={setPreview}
                />
                {p.evidenceRequest && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {p.evidenceRequest.requestedBy ?? 'Finance'} requested{' '}
                    {p.evidenceRequest.kind === 'ADDITIONAL' ? 'an additional' : 'a replacement'} receipt on{' '}
                    {formatDateTime(p.evidenceRequest.requestedAt)}: {p.evidenceRequest.reason}. Waiting for the upload.
                  </Alert>
                )}
              </Box>
              {p.reviewHistory.length > 0 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Review history
                  </Typography>
                  <Table size="small" aria-label="Review history">
                    <TableBody>
                      {p.reviewHistory.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>{formatDateTime(e.at)}</TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <strong>{HISTORY_LABEL[e.action]}</strong> by {e.by}
                            {e.selfApproved ? ' (self-approved)' : ''}
                            {e.receiptNumber ? ` · ${e.action === 'REVERSED' ? 'voided' : 'receipt'} ${e.receiptNumber}` : ''}
                            {e.reason ? ` — ${e.reason}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {canReverse && reversing && (
                <TextField
                  label="Reversal reason"
                  helperText="Required. The payment stops counting as paid, its receipt is voided (kept), and the recorder is notified."
                  fullWidth
                  multiline
                  minRows={2}
                  autoFocus
                  inputProps={{ maxLength: 500 }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              {canReview && rejecting && (
                <TextField
                  label="Rejection reason"
                  helperText="Required — the counselor sees this and corrects the payment."
                  fullWidth
                  multiline
                  minRows={2}
                  autoFocus
                  inputProps={{ maxLength: 500 }}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
              {mutationError && (
                <Alert severity="error">
                  {errorMessage(mutationError, "Couldn't save the decision.")}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Close
          </Button>
          {canDelete && !rejecting && (
            <Button color="error" variant="outlined" onClick={() => setDeleteOpen(true)} disabled={busy}>
              Delete payment
            </Button>
          )}
          {p && canReverse && !reversing && (
            <Button color="warning" variant="outlined" onClick={() => setReversing(true)} disabled={busy}>
              Reverse approval
            </Button>
          )}
          {p && canReverse && reversing && (
            <>
              <Button onClick={() => setReversing(false)} disabled={busy}>
                Back
              </Button>
              <Button variant="contained" color="warning" disabled={busy || !reason.trim()} onClick={() => reverseMutation.mutate(p.id)}>
                {reverseMutation.isPending ? 'Reversing…' : 'Reverse approval'}
              </Button>
            </>
          )}
          {p && canReview && !rejecting && (
            <>
              <Button color="error" onClick={() => setRejecting(true)} disabled={busy}>
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={busy}
                onClick={() => approveMutation.mutate(p.id)}
              >
                {approveMutation.isPending
                  ? 'Approving…'
                  : `${p.status === 'REVERSED' ? 'Approve again' : 'Approve'}${p.type === 'PAYMENT' ? ' & issue receipt' : ''}`}
              </Button>
            </>
          )}
          {p && canReview && rejecting && (
            <>
              <Button onClick={() => setRejecting(false)} disabled={busy}>
                Back
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={busy || !reason.trim()}
                onClick={() => rejectMutation.mutate(p.id)}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject payment'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <DocumentPreviewDialog target={preview} onClose={() => setPreview(null)} />
      <DeletePaymentDialog
        payment={deleteOpen && p ? { id: p.id, paymentNumber: p.paymentNumber } : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(payment) => {
          setDeleteOpen(false);
          onDecided(payment, 'deleted');
          close();
        }}
      />
      <RequestReceiptDialog
        payment={requestOpen && p ? { id: p.id, paymentNumber: p.paymentNumber } : null}
        onClose={() => setRequestOpen(false)}
        onDone={() => void query.refetch()}
      />
    </>
  );
};
